import { useS3Download } from '@doltech/core/lib/hooks/useS3Download';
import {
  FlatAudioPlayer,
  FlatPlayerInstance,
} from '@doltech/ui/lib/figma/Audio/Player/FlatAudioPlayer';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { SkillEnum } from '@doltech/utils/lib/constants';
import { scrollToQuestion } from '@doltech/utils/lib/scrollUtils';
import { Spin } from 'antd';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import * as React from 'react';

import { useControlledAudioPlayInBackgroundStore } from '@doltech/ui/lib/hooks/controlled-audio-play-in-background.store';
import { useCountDownTimer } from '@doltech/ui/lib/hooks/useCountDownTimer';
import { submitTest } from '../api/do-test';
import { useListeningAudioControlledStore } from '../hooks/useListeningAudioControlledStore';
import { useListeningFullTestModal } from '../hooks/useListeningFullTestModal';
import { ListeningMobileGuidedTour } from '../layouts/GuidedTour/ListeningMobileGuidedTour';
import { ReadingTestSubmissionProps } from '../layouts/ReadingTestSubmission';
import { useTestSubmissionStore } from '../model/test-submissions.hook';
import { DoTestFooter } from './DoTest/Reading/Footer/DoTestFooter';
import { DoTestHeader } from './DoTest/Reading/Header/DoTestHeader';
import { ListeningOverallDrawerModal } from './Modal/ListeningOverallDrawerModal';
import { QuestionContentMobile } from './Question/QuestionContentMobile';
import { TestMobileSubmission } from './TestMobileSubmission';

export const ListeningTestSubmissionMobile = observer((props: ReadingTestSubmissionProps) => {
  const { onSubmit, onSubmitWithEvents, onSubmitSuccess, disableSubmitAction, onQuit } = props;
  const testSubmissionStore = useTestSubmissionStore();
  const currentSection = testSubmissionStore.currentTestSection;
  const audioPlayerRef = React.useRef<FlatPlayerInstance>();
  const s3AudioData = useS3Download(testSubmissionStore.currentTestSection?.script?.audio?.path);
  const [showOverall, setShowOverall] = React.useState(false);
  const toggleShowOverall = React.useCallback(() => {
    setShowOverall((prevShowOverall) => !prevShowOverall);
  }, []);
  const audioStore = useControlledAudioPlayInBackgroundStore();
  const { openModalConfirmSubmit } = useListeningFullTestModal();

  const {
    inProgressTotalDurationInSeconds,
    currentAudioSection,
    id,
    timeTakenInSeconds,
    isFullTest,
    totalDurationInSeconds,
    setRemainTimeInSeconds,
    prepareDataForSubmit,
    audio,
  } = testSubmissionStore;

  const handleSubmitTest = async () => {
    submitTest({ testId: id, userAnswers: prepareDataForSubmit() });
  };

  useListeningAudioControlledStore({
    audioPath: isFullTest ? audio?.path : currentAudioSection?.script?.audio?.path,
  });

  const { startTimer, stopTimer } = useCountDownTimer(
    inProgressTotalDurationInSeconds as any,
    (timeInSeconds) => {
      setRemainTimeInSeconds(timeInSeconds);

      if (!audioStore?.playing && timeTakenInSeconds + 1 < audioStore.durationInSeconds) {
        audioStore?.play?.(timeTakenInSeconds);
      }

      if (!isFullTest) return;

      if (timeInSeconds <= 0) {
        audioStore?.pause?.();
        stopTimer();
        handleSubmitTest();

        openModalConfirmSubmit({
          title: 'Hết giờ',
          description: 'Đã hết giờ làm bài. Hệ thống sẽ tự động nộp bài.',
          primaryButton: {
            name: 'OK',
            onClick: onSubmitSuccess,
          },
        });
      }
    },
    1000
  );

  React.useEffect(() => {
    if (testSubmissionStore.isStartedDoingTest) {
      if (timeTakenInSeconds < audioStore.durationInSeconds) {
        audioStore.play(timeTakenInSeconds);
      }
      startTimer();
      testSubmissionStore.startSection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testSubmissionStore.isStartedDoingTest]);

  const goToQuestion = (qUuid, testSectionIndex, gIndex) => {
    testSubmissionStore.goToQuestionAtGroup(testSectionIndex, gIndex);
    toggleShowOverall();
    // this magic use set time out to make sure question has been render after move to another test sections
    setTimeout(() => {
      scrollToQuestion(qUuid);
    });
  };

  if (isNil(currentSection) || testSubmissionStore.initialed === false) return null;

  return (
    <TestMobileSubmission
      skill={SkillEnum.LISTENING}
      background={colorsV2.white100}
      onSubmit={onSubmit}
      onSubmitWithEvents={onSubmitWithEvents}
      disableSubmitAction={disableSubmitAction}
      enableEvent
    >
      <DoTestHeader
        showQuitButton
        onQuit={onQuit}
        onSubmit={onSubmitWithEvents}
        totalPassage={getSnapshot(testSubmissionStore.testSections).length}
        totalTime={testSubmissionStore.totalDuration}
        testName={testSubmissionStore.name}
        testSectionName={currentSection.name}
      />

      <div id="body-container" className="visible">
        <QuestionContentMobile onSubmit={onSubmitWithEvents} />
      </div>
      <div className="audio-player-wrapper">
        <Spin size="small" spinning={s3AudioData.isLoading}>
          <FlatAudioPlayer
            startTimeInSeconds={testSubmissionStore?.currentTestSection?.testTakenTime || 0}
            ref={audioPlayerRef}
            volume={1}
            url={null}
            testTimeTakenInSeconds={timeTakenInSeconds}
            testDurationInSeconds={totalDurationInSeconds}
            customSlider
          />
        </Spin>
      </div>

      <DoTestFooter showOverall={() => toggleShowOverall()} />
      <ListeningMobileGuidedTour setShowOverall={setShowOverall} />

      <ListeningOverallDrawerModal
        onSubmit={() => {
          if (testSubmissionStore.preventUserAction()) return;
          toggleShowOverall();
          onSubmitWithEvents();
        }}
        goToQuestion={goToQuestion}
        onClose={() => toggleShowOverall()}
        visible={showOverall}
      />
    </TestMobileSubmission>
  );
});
